import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Database } from '@/integrations/supabase/types';

type Organization = Database['public']['Tables']['organizations']['Row'];
type OrganizationInsert = Database['public']['Tables']['organizations']['Insert'];
type OrganizationUpdate = Database['public']['Tables']['organizations']['Update'];

export class OrganizationsService {
  private static readonly TABLE = 'organizations';
  private static readonly logger = logger.child({ service: 'OrganizationsService' });

  /**
   * Get all organizations for the current user
   */
  static async getAll() {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching all organizations');

      const { data, error } = await supabase
        .from('organizations')
        .select('*, user_roles!inner(role)')
        .order('name', { ascending: true });

      if (error) throw error;

      this.logger.info('Organizations fetched', { count: data?.length || 0 });

      return data || [];
    }, { operation: 'getAll', table: this.TABLE });
  }

  /**
   * Get a single organization by ID
   */
  static async getById(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching organization', { organizationId: id });

      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Organization fetched', { organizationId: id, found: !!data });

      return data;
    }, { operation: 'getById', table: this.TABLE });
  }

  /**
   * Create a new organization
   */
  static async create(organization: OrganizationInsert, userId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Creating organization', { name: organization.name, userId });

      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert(organization)
        .select()
        .maybeSingle();

      if (orgError) throw orgError;

      // Assign owner role to creator
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          organization_id: org.id,
          role: 'owner',
        });

      if (roleError) throw roleError;

      this.logger.info('Organization created', { organizationId: org.id });

      return org;
    }, { operation: 'create', table: this.TABLE });
  }

  /**
   * Update an organization
   */
  static async update(id: string, updates: OrganizationUpdate) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating organization', { organizationId: id });

      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Organization updated', { organizationId: id });

      return data;
    }, { operation: 'update', table: this.TABLE });
  }

  /**
   * Get subscription for an organization
   */
  static async getSubscription(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching subscription', { organizationId });

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Subscription fetched', { organizationId, found: !!data });

      return data;
    }, { operation: 'getSubscription', table: 'subscriptions' });
  }

  /**
   * Upload organization logo
   */
  static async uploadLogo(organizationId: string, file: File) {
    return withServiceErrorHandling(async () => {
      // Validate file
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        throw ServiceError.validation('Invalid file type. Please upload a JPG, PNG, WEBP, or SVG image.');
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw ServiceError.validation('File size must be less than 5MB');
      }

      this.logger.info('Uploading logo', { organizationId, fileSize: file.size });

      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${organizationId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('brand-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('brand-logos')
        .getPublicUrl(filePath);

      // Update organization with logo URL
      const updated = await this.update(organizationId, { logo_url: publicUrl });

      this.logger.info('Logo uploaded', { organizationId, filePath });

      return updated;
    }, { operation: 'uploadLogo', table: this.TABLE });
  }

  /**
   * Delete organization logo
   */
  static async deleteLogo(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Deleting logo', { organizationId });

      const org = await this.getById(organizationId);
      if (!org?.logo_url) {
        throw ServiceError.validation('Organization has no logo to delete');
      }

      // Extract file path from URL
      const urlParts = org.logo_url.split('/');
      const fileName = urlParts[urlParts.length - 1];

      // Delete from storage
      const { error: deleteError } = await supabase.storage
        .from('brand-logos')
        .remove([fileName]);

      if (deleteError) throw deleteError;

      // Update organization to remove logo URL
      const updated = await this.update(organizationId, { logo_url: null });

      this.logger.info('Logo deleted', { organizationId });

      return updated;
    }, { operation: 'deleteLogo', table: this.TABLE });
  }

  /**
   * Update organization shortcode (admin-only)
   * @param organizationId - Organization UUID
   * @param newShortcode - 3-5 lowercase letters
   */
  static async updateShortcode(organizationId: string, newShortcode: string) {
    return withServiceErrorHandling(async () => {
      // Validate format: 3-5 lowercase letters, no spaces or dashes
      if (!/^[a-z]{3,5}$/.test(newShortcode)) {
        throw ServiceError.validation('Shortcode must be 3-5 lowercase letters (no spaces or dashes)');
      }

      this.logger.info('Updating shortcode', { organizationId, newShortcode });

      const { data, error } = await supabase
        .from('organizations')
        .update({ shortcode: newShortcode })
        .eq('id', organizationId)
        .select()
        .maybeSingle();

      if (error) {
        if (error.code === '23505') { // Unique violation
          throw ServiceError.conflict('This shortcode is already taken');
        }
        throw error;
      }

      this.logger.info('Shortcode updated', { organizationId, newShortcode });

      return data;
    }, { operation: 'updateShortcode', table: this.TABLE });
  }

  /**
   * Get usage statistics for an organization with pagination
   */
  static async getUsageStats(organizationId: string, days: number = 30, limit: number = 1000) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching usage stats', { organizationId, days, limit });

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('usage_logs')
        .select('feature, quantity, created_at')
        .eq('organization_id', organizationId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Aggregate by feature
      const usageByFeature: Record<string, number> = {};
      data?.forEach(log => {
        usageByFeature[log.feature] = (usageByFeature[log.feature] || 0) + log.quantity;
      });

      const stats = {
        logs: data || [],
        summary: usageByFeature,
        totalUsage: data?.reduce((sum, log) => sum + log.quantity, 0) || 0,
      };

      this.logger.info('Usage stats calculated', { 
        organizationId, 
        totalUsage: stats.totalUsage 
      });

      return stats;
    }, { operation: 'getUsageStats', table: 'usage_logs' });
  }
}
